import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TextInput from "../common/TextInput";
import { FIELD_LIMITS } from "../common/fieldLimits";
import tailwindClassnames from "../common/TailwindClassnames";
import SelectInput from "../common/SelectInput";
import {
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
} from "@mui/material";
import Radio from "@mui/material/Radio";
import {
  DeploymentResponseDto,
  DeploymentTemplateResponseDto,
  DropDownDto,
  ProjectCreateDto,
  TaxonomyResponseDto,
} from "../common/ObjectTypes";
import PasswordInput from "../common/PasswordInput";
import { useProfile } from "../common/ProfileProvider";
import {
  BaitType,
  YesSomeNo,
  CaptureMethod,
  OrganizationRoles,
  ProjectRoles,
  SensorLayouts,
  PhotosLicense,
  MetadataLicense,
} from "../common/Enums";
import Tooltip from "@mui/material/Tooltip";
import { BeatLoader } from "react-spinners";
import DateSelector from "../common/DateSelector";
import { InfoIcon } from "../common/ProjectIcons";
import {
  formatDateAsLocalString,
  parseLocalDateStringToDate,
} from "../common/helpers";

/**
 * A react-select option for the project timezone picker. Value and label are both the raw
 * IANA identifier: it is what the API stores, what CamTrap DP consumers expect, and what the
 * user needs to read. Declared here rather than reusing DropDownDto, whose `value` is a
 * number.
 */
type TimezoneOption = {
  value: string;
  label: string;
};

type ProjectDetailsOverviewProps = {
  projectId: number | undefined;
  orgId: number | undefined;
  profile: any;
  formData: ProjectCreateDto;
  deploymentTemplates: DeploymentTemplateResponseDto[];
  deployments?: DeploymentResponseDto[]; // Added deployments prop
  perms: any[];
  canSetSecret: boolean;
  secretIsSet: boolean;
  updatingSasToken: boolean;
  setSasToken: (sasToken: string) => void;
  setUpdatingSasToken: (updatingSecret: boolean) => void;
  onSelectOrganization: (e: any) => void;
  onSelectCountry: (e: React.FormEvent) => void;
  timezones: string[];
  timezonesLoading: boolean;
  onSelectTimezone: (selectedOption: TimezoneOption | null) => void;
  onStorageOptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onObservationLevelChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSpeciesFocusChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectSensorLayout: (e: React.FormEvent) => void;
  onSelectCaptureMethod: (e: React.FormEvent) => void;
  onSelectBaitUsed: (e: React.FormEvent) => void;
  onSelectBaitType: (e: React.FormEvent) => void;
  onSelectBlankImagesRemoved: (e: React.FormEvent) => void;
  onSelectPhotosLicense: (e: React.FormEvent) => void;
  onSelectMetadataLicense: (e: React.FormEvent) => void;
  onSelectSchemaTemplate: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCancel: () => void;
  onSave: (e: React.FormEvent) => void;
  isSaving: boolean;
  saveDisabled: boolean;
  existingProject: boolean;
  schemaLoading: boolean;
  organizationOptions: DropDownDto[];
  sasToken: string;
  countries: any[];
  errors: any;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  taxonomy: TaxonomyResponseDto[];
  onSelectFocalSpecies: (species: any) => void;
  focalSpeciesSearching: boolean;
  onTaxonomySearch: (
    inputValue: string,
    callback: (options: any[]) => void,
  ) => Promise<void> | undefined;
  loadingFocalSpecies: boolean;
  defaultFocalSpecies: TaxonomyResponseDto | null;
};

const ProjectDetailsOverview: React.FC<ProjectDetailsOverviewProps> = ({
  projectId,
  orgId,
  profile,
  formData,
  deploymentTemplates,
  deployments = [], // Add deployments with default empty array
  perms,
  canSetSecret,
  secretIsSet,
  updatingSasToken,
  setSasToken,
  setUpdatingSasToken,
  onSelectOrganization,
  onSelectCountry,
  timezones,
  timezonesLoading,
  onSelectTimezone,
  onStorageOptionChange,
  onObservationLevelChange,
  onSpeciesFocusChange,
  onSelectSensorLayout,
  onSelectCaptureMethod,
  onSelectBaitUsed,
  onSelectBaitType,
  onSelectBlankImagesRemoved,
  onSelectPhotosLicense,
  onSelectMetadataLicense,
  onSelectSchemaTemplate,
  onCancel,
  onSave,
  isSaving,
  saveDisabled,
  schemaLoading,
  existingProject,
  organizationOptions,
  sasToken,
  countries,
  errors,
  onChange,
  onSelectFocalSpecies,
  focalSpeciesSearching,
  onTaxonomySearch,
  loadingFocalSpecies,
  defaultFocalSpecies,
}: ProjectDetailsOverviewProps) => {
  const { t } = useTranslation();

  const canEditProjectDetails = perms.some(
    (permission: any) => permission.name === "EDIT_PROJECT_DETAILS",
  );

  const countryId = formData.country_id;
  const getCountryByCode = (code: any) => {
    return countries.find((country) => country.code === code);
  };
  const defaultCountry = getCountryByCode(countryId);

  const { theme } = useProfile();
  const isDarkMode = theme === "dark";
  const getCanEditProjectOrganization = () => {
    if (profile?.is_admin) {
      return true;
    }
    const isOrgOwnerOrNull =
      profile?.org_roles.some(
        (role: any) =>
          role.org_id === orgId && role.role === OrganizationRoles.OWNER,
      ) || orgId === undefined;

    const isProjOwner = profile?.project_roles.some(
      (role: any) =>
        role.project_id === projectId && role.role === ProjectRoles.OWNER,
    );
    return isOrgOwnerOrNull && isProjOwner;
  };
  const getIsReadOnlyUser = () => {
    const isContributorViewerOrTagger = profile?.project_roles.some(
      (project_roles: any) =>
        project_roles.project_id === projectId &&
        (project_roles.role === ProjectRoles.CONTRIBUTOR ||
          project_roles.role === ProjectRoles.VIEWER ||
          project_roles.role === ProjectRoles.TAGGER),
    );
    return isContributorViewerOrTagger;
  };

  /**
   * Sets the date to be a string in the format YYYY-MM-DD.
   * @param name the name of the date field
   * @param date the date to be set
   */
  const handleDateChange = (e: any) => {
    onChange({
      target: {
        name: e?.target?.name,
        value: formatDateAsLocalString(e?.target?.value),
      },
    } as any);
  };

  const [hasDeploymentsWithData, setHasDeploymentsWithData] =
    useState<boolean>(false);

  // Effect to check for deployments with schema data and update state when deployments change
  useEffect(() => {
    const hasSchemaData = deployments.some(
      (d) => d.schema_data && Object.keys(d.schema_data).length > 0,
    );
    setHasDeploymentsWithData(hasSchemaData);
  }, [deployments]);

  /**
   * Renders the shared MUI info tooltip (hover `InfoIcon`) used next to field
   * labels. Kept as a single helper so every info icon on the page matches.
   * @param title tooltip content (string or node)
   * @param wide when true, removes the tooltip max-width cap (used for the
   *   long license explanation)
   */
  const infoTooltip = (title: React.ReactNode, wide = false) => (
    <Tooltip
      title={title}
      placement="right"
      arrow
      slotProps={{
        tooltip: {
          sx: { fontSize: "1em", ...(wide ? { maxWidth: "none" } : {}) },
        },
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
    >
      <span className="ml-0.5 cursor-help flex items-center -my-1">
        <InfoIcon />
      </span>
    </Tooltip>
  );

  // License tooltip content for Photos License and Metadata License fields
  const licenseTooltipContent = (
    <div style={{ maxWidth: "600px" }}>
      <div className="mb-1">
        <strong>Creative Commons Zero (CC0)</strong> permits a user to share,
        adapt and modify the work, even for commercial purposes, without asking
        permission.
        <ul className="list-disc pl-4 mt-1">
          <li>
            Summary:{" "}
            <a
              href="https://creativecommons.org/publicdomain/zero/1.0/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              https://creativecommons.org/publicdomain/zero/1.0/
            </a>
          </li>
          <li>
            Full legal text:{" "}
            <a
              href="https://creativecommons.org/publicdomain/zero/1.0/legalcode"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              https://creativecommons.org/publicdomain/zero/1.0/legalcode
            </a>
          </li>
        </ul>
      </div>
      <div className="mb-1">
        <strong>Creative Commons Attribution 4.0 (CC BY 4.0)</strong>, which
        permits a data user to share and adapt material with appropriate
        attribution, including for commercial purposes.
        <ul className="list-disc pl-4 mt-1">
          <li>
            Summary:{" "}
            <a
              href="https://creativecommons.org/licenses/by/4.0/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              https://creativecommons.org/licenses/by/4.0/
            </a>
          </li>
          <li>
            Full legal text:{" "}
            <a
              href="https://creativecommons.org/licenses/by/4.0/legalcode"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              https://creativecommons.org/licenses/by/4.0/legalcode
            </a>
          </li>
        </ul>
      </div>
      <div>
        <strong>
          Creative Commons Attribution-NonCommercial 4.0 (CC BY-NC 4.0)
        </strong>
        , which permits a data user to share and adapt material with appropriate
        attribution, only for noncommercial purposes.
        <ul className="list-disc pl-4 mt-1">
          <li>
            Summary:{" "}
            <a
              href="https://creativecommons.org/licenses/by-nc/4.0/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              https://creativecommons.org/licenses/by-nc/4.0/
            </a>
          </li>
          <li>
            Full legal text:{" "}
            <a
              href="https://creativecommons.org/licenses/by-nc/4.0/legalcode"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              https://creativecommons.org/licenses/by-nc/4.0/legalcode
            </a>
          </li>
        </ul>
      </div>
    </div>
  );

  // Standard text-input editability + styling shared across most fields.
  const inputEditable = canEditProjectDetails || !existingProject;
  const inputClassName = inputEditable
    ? tailwindClassnames.detailsPageTextInput
    : tailwindClassnames.detailsPageTextInputDisabled;

  return (
    <div className="w-full">
      <form className="mx-8 pt-4 text-left">
        <div className={tailwindClassnames.formSectionStack}>
          {/* SECTION: General */}
          <section className={tailwindClassnames.formSectionCard}>
            <div className={tailwindClassnames.formSectionHeader}>
              <div className={tailwindClassnames.formSectionTitle}>
                {t("General")}
              </div>
            </div>
            <div className={tailwindClassnames.formGrid}>
              <div className={tailwindClassnames.formField}>
                <TextInput
                  name="project_name"
                  label={t("Project Name") + "*"}
                  placeholder=""
                  value={formData.project_name}
                  error={errors.project_name}
                  className={inputClassName}
                  type="text"
                  initialFocus={true}
                  limit={FIELD_LIMITS.PROJECT_NAME}
                  onChange={onChange}
                  disabled={!inputEditable}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <TextInput
                  name="short_name"
                  label={t("Short Name") + "*"}
                  labelSuffix={infoTooltip(
                    t("A short name that uniquely identifies the project"),
                  )}
                  placeholder=""
                  value={formData.short_name}
                  error={errors.short_name}
                  className={inputClassName}
                  type="text"
                  limit={FIELD_LIMITS.SHORT_NAME}
                  onChange={onChange}
                  disabled={!inputEditable}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <TextInput
                  name="acronym"
                  label={t("Acronym")}
                  placeholder=""
                  value={formData.acronym || ""}
                  error={errors.acronym}
                  className={inputClassName}
                  type="text"
                  initialFocus={true}
                  limit={FIELD_LIMITS.TEXT}
                  onChange={onChange}
                  disabled={!inputEditable}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <SelectInput
                  options={organizationOptions}
                  className={"w-full"}
                  value={
                    existingProject &&
                    formData.org_id &&
                    organizationOptions.length > 0
                      ? organizationOptions.find(
                          (org) => org.value === formData.org_id,
                        ) || undefined
                      : !existingProject &&
                          orgId &&
                          organizationOptions.length > 0
                        ? organizationOptions.find(
                            (org) => org.value === orgId,
                          ) || undefined
                        : undefined
                  }
                  isDisabled={
                    existingProject && !getCanEditProjectOrganization()
                  }
                  isLoading={false}
                  errors={errors.org_id}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectOrganization}
                  name={t("Organization") + "*"}
                  searchBar={false}
                />
              </div>
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <div className="flex items-center gap-2">
                  <input
                    id="inherit_permissions"
                    className="accent-storm-blue dark:accent-sky-blue cursor-pointer transition duration-300"
                    type="checkbox"
                    name="inherit_permissions"
                    checked={formData.inherit_permissions}
                    onChange={onChange}
                    disabled={existingProject && !canEditProjectDetails}
                  />
                  <label
                    htmlFor="inherit_permissions"
                    className="text-primary-off-black dark:text-primary-off-white transition duration-300"
                  >
                    {t("Inherit User Roles from Organization")}
                  </label>
                  {infoTooltip(
                    t(
                      "If checked, certain Project users will inherit permissions based on their role in the Organization. Organization Owners will become Project Editors, and Organization Editors will become Project Viewers (unless they already have roles for the Project).",
                    ),
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* SECTION: Contact & Description */}
          <section className={tailwindClassnames.formSectionCard}>
            <div className={tailwindClassnames.formSectionHeader}>
              <div className={tailwindClassnames.formSectionTitle}>
                {t("Contact & Description")}
              </div>
            </div>
            <div className={tailwindClassnames.formGrid}>
              <div className={tailwindClassnames.formField}>
                <TextInput
                  name="primary_contact_name"
                  label={t("Project Admin") + "*"}
                  placeholder=""
                  value={formData.primary_contact_name}
                  error={errors.primary_contact_name}
                  className={inputClassName}
                  type="text"
                  initialFocus={true}
                  limit={FIELD_LIMITS.TEXT}
                  onChange={onChange}
                  disabled={!inputEditable}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <TextInput
                  name="primary_contact_email"
                  label={t("Project Admin Email") + "*"}
                  placeholder=""
                  value={
                    formData.primary_contact_email
                      ? formData.primary_contact_email
                      : ""
                  }
                  error={errors.primary_contact_email}
                  disabled={!inputEditable}
                  className={inputClassName}
                  type="text"
                  initialFocus={false}
                  limit={FIELD_LIMITS.EMAIL}
                  onChange={onChange}
                />
              </div>
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <TextInput
                  name="website"
                  label={t("Website")}
                  placeholder=""
                  value={formData.website ? formData.website : ""}
                  error={errors.website}
                  disabled={!inputEditable}
                  className={inputClassName}
                  type="text"
                  initialFocus={false}
                  limit={FIELD_LIMITS.TEXT}
                  onChange={onChange}
                />
              </div>
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <TextInput
                  name="description"
                  label={t("Description")}
                  placeholder=""
                  value={formData.description ? formData.description : ""}
                  error={errors.description}
                  disabled={!inputEditable}
                  className={inputClassName}
                  type="text"
                  initialFocus={false}
                  onChange={onChange}
                  softLimit={FIELD_LIMITS.NARRATIVE}
                  rows={4}
                  resizable={true}
                />
              </div>
            </div>
          </section>

          {/* SECTION: Scope & Timeline */}
          <section className={tailwindClassnames.formSectionCard}>
            <div className={tailwindClassnames.formSectionHeader}>
              <div className={tailwindClassnames.formSectionTitle}>
                {t("Scope & Timeline")}
              </div>
            </div>
            <div className={tailwindClassnames.formGrid}>
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <TextInput
                  name="objectives"
                  label={t("Objectives") + "*"}
                  placeholder=""
                  value={formData.objectives ? formData.objectives : ""}
                  error={errors.objectives}
                  disabled={!inputEditable}
                  className={inputClassName}
                  type="text"
                  initialFocus={false}
                  onChange={onChange}
                  // AKL-728: the column is TEXT now (Wildlife Insights treats
                  // objectives as required, unbounded narrative text), so this is a
                  // counter rather than a cap -- long text is shown, never clipped.
                  softLimit={FIELD_LIMITS.NARRATIVE}
                  rows={4}
                  resizable={true}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <SelectInput
                  options={countries.map((country) => ({
                    value: country.code,
                    label: country.name,
                  }))}
                  className={"w-full"}
                  defaultValue={
                    defaultCountry
                      ? {
                          value: defaultCountry.code,
                          label: defaultCountry.name,
                        }
                      : undefined
                  }
                  isDisabled={!inputEditable}
                  errors={errors.country_id}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectCountry}
                  name={t("Country") + "*"}
                  searchBar={false}
                />
              </div>
              {/* Occupies the grid cell that used to hold a bare spacer, so Timezone sits
                  beside Country and Start/End Date still pair on their own row. */}
              <div className={tailwindClassnames.formField}>
                <SelectInput
                  options={timezones.map((timezone) => ({
                    value: timezone,
                    label: timezone,
                  }))}
                  className={"w-full"}
                  // Controlled rather than defaultValue (as Country uses): the project loads
                  // asynchronously, and a defaultValue would be captured before the fetch
                  // resolves and never update. A legacy project's null renders as an empty
                  // select, not the string "null".
                  value={
                    formData.timezone
                      ? { value: formData.timezone, label: formData.timezone }
                      : null
                  }
                  isDisabled={!inputEditable}
                  errors={errors.timezone}
                  isLoading={timezonesLoading}
                  isClearable={true}
                  // Not optional in practice — there are ~600 IANA identifiers.
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectTimezone}
                  name={t("Project Timezone") + "*"}
                  searchBar={false}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <DateSelector
                  label={`${t("Start Date")}`}
                  name="start_date"
                  date={
                    formData.start_date
                      ? parseLocalDateStringToDate(formData.start_date)
                      : null
                  }
                  onDateChange={handleDateChange}
                  maxDate={
                    parseLocalDateStringToDate(formData.end_date) ?? undefined
                  }
                  disabled={false}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <DateSelector
                  label={`${t("End Date")}`}
                  name="end_date"
                  date={
                    formData.end_date
                      ? parseLocalDateStringToDate(formData.end_date)
                      : null
                  }
                  onDateChange={handleDateChange}
                  disabled={false}
                />
                {errors.end_date && (
                  <p className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2 my-1">
                    {errors.end_date}
                  </p>
                )}
              </div>
              <div className={tailwindClassnames.formField}>
                <FormControl>
                  <FormLabel
                    id="observation-level-radio-buttons-group"
                    sx={{
                      color: isDarkMode
                        ? tailwindClassnames.primaryOffWhite
                        : tailwindClassnames.offBlackLight,
                    }}
                  >
                    {t("Observation Level") + "*"}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="observation-level-radio-buttons-group"
                    name="observation-level-radio-buttons-group"
                    value="false"
                    onChange={onObservationLevelChange}
                  >
                    <FormControlLabel
                      value="media"
                      control={
                        <Radio
                          sx={{
                            color: "grey",
                            "&.Mui-checked": {
                              color: isDarkMode
                                ? tailwindClassnames.skyBlue
                                : tailwindClassnames.stormBlue,
                            },
                            "&.Mui-disabled": {
                              color: tailwindClassnames.greyedText,
                            },
                          }}
                          disabled={true}
                        />
                      }
                      className={
                        canSetSecret
                          ? tailwindClassnames.textInputLabel
                          : tailwindClassnames.textInputLabelDisabled
                      }
                      checked={
                        formData.observation_level === "media" ? true : false
                      }
                      label={t("Media")}
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          color: isDarkMode
                            ? tailwindClassnames.skyBlue
                            : tailwindClassnames.stormBlue,
                          "&.Mui-disabled": {
                            color: tailwindClassnames.greyedText,
                          },
                        },
                      }}
                      disabled={true}
                    />
                  </RadioGroup>
                </FormControl>
              </div>
              <div className={tailwindClassnames.formField}>
                <FormControl>
                  <FormLabel
                    id="species-focus-radio-buttons-group"
                    sx={{
                      color: isDarkMode
                        ? tailwindClassnames.primaryOffWhite
                        : tailwindClassnames.offBlackLight,
                    }}
                  >
                    {t("Species Focus") + "*"}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="species-focus-radio-buttons-group"
                    name="species-focus-radio-buttons-group"
                    value="single"
                    onChange={onSpeciesFocusChange}
                  >
                    <FormControlLabel
                      value="single"
                      control={
                        <Radio
                          sx={{
                            color: "grey",
                            "&.Mui-checked": {
                              color: isDarkMode
                                ? tailwindClassnames.skyBlue
                                : tailwindClassnames.stormBlue,
                            },
                          }}
                        />
                      }
                      className={
                        canSetSecret
                          ? tailwindClassnames.textInputLabel
                          : tailwindClassnames.textInputLabelDisabled
                      }
                      checked={formData.species_focus === "single"}
                      label={t("Single")}
                    />
                    <FormControlLabel
                      value="multiple"
                      control={
                        <Radio
                          sx={{
                            color: "grey",
                            "&.Mui-checked": {
                              color: isDarkMode
                                ? tailwindClassnames.skyBlue
                                : tailwindClassnames.stormBlue,
                            },
                          }}
                        />
                      }
                      className={
                        canSetSecret
                          ? tailwindClassnames.textInputLabel
                          : tailwindClassnames.textInputLabelDisabled
                      }
                      checked={formData.species_focus === "multiple"}
                      label={t("Multiple")}
                    />
                  </RadioGroup>
                </FormControl>
              </div>
              {formData.species_focus === "single" && (
                <div
                  className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
                >
                  <SelectInput
                    options={[]} // Empty initial options - will be loaded via async
                    className={"w-full"}
                    defaultValue={
                      defaultFocalSpecies
                        ? {
                            value: defaultFocalSpecies.id,
                            label:
                              defaultFocalSpecies.common_name_english +
                              `${
                                defaultFocalSpecies.scientific_name
                                  ? ` (${defaultFocalSpecies.scientific_name})`
                                  : ""
                              }`,
                          }
                        : undefined
                    }
                    isDisabled={!inputEditable}
                    errors={errors.focal_species}
                    isLoading={focalSpeciesSearching || loadingFocalSpecies}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                    onChange={onSelectFocalSpecies}
                    name={t("Focal Species")}
                    searchBar={false}
                    isAsync={true}
                    loadOptions={(inputValue, callback) =>
                      onTaxonomySearch(inputValue, callback)
                    }
                    placeholder={t("Search for species...")}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue && inputValue.length > 0
                        ? t("No species found matching '{{inputValue}}'", {
                            inputValue,
                          })
                        : t("Start typing to search for species...")
                    }
                  />
                </div>
              )}
            </div>
          </section>

          {/* SECTION: Survey Design */}
          <section className={tailwindClassnames.formSectionCard}>
            <div className={tailwindClassnames.formSectionHeader}>
              <div className={tailwindClassnames.formSectionTitle}>
                {t("Survey Design")}
              </div>
            </div>
            <div className={tailwindClassnames.formGrid}>
              <div className={tailwindClassnames.formField}>
                <SelectInput
                  options={SensorLayouts.map((layout) => ({
                    value: layout.value,
                    label: t(layout.name),
                  }))}
                  className={"w-full"}
                  defaultValue={
                    formData.sensor_layout
                      ? {
                          value: formData.sensor_layout,
                          label: t(
                            SensorLayouts.find(
                              (layout) =>
                                layout.value === formData.sensor_layout,
                            )?.name || "",
                          ),
                        }
                      : null
                  }
                  isDisabled={!inputEditable}
                  errors={errors.sensor_layout}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectSensorLayout}
                  name={t("Sensor Layout") + "*"}
                  searchBar={false}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <SelectInput
                  options={CaptureMethod.map((method) => ({
                    value: method.value,
                    label: t(method.name),
                  }))}
                  className={"w-full"}
                  defaultValue={
                    formData.capture_method
                      ? {
                          value: formData.capture_method,
                          label: t(
                            CaptureMethod.find(
                              (method) =>
                                method.value === formData.capture_method,
                            )?.name || "",
                          ),
                        }
                      : null
                  }
                  isDisabled={!inputEditable}
                  errors={errors.capture_method}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectCaptureMethod}
                  name={t("Capture Method") + "*"}
                  searchBar={false}
                />
              </div>
              {formData.sensor_layout === "targeted" && (
                <div className={tailwindClassnames.formField}>
                  <TextInput
                    name="sensor_layout_target"
                    label={t("Sensor Layout Target")}
                    placeholder={t("Specify the Target")}
                    value={formData.sensor_layout_target || ""}
                    error={errors.sensor_layout_target}
                    disabled={!inputEditable}
                    className={inputClassName}
                    type="text"
                    initialFocus={false}
                    softLimit={FIELD_LIMITS.LONG_LINE}
                    onChange={onChange}
                  />
                </div>
              )}
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    id="sensors_clustered"
                    className="mt-0.5 accent-storm-blue dark:accent-sky-blue cursor-pointer transition duration-300"
                    type="checkbox"
                    name="sensors_clustered"
                    checked={formData.sensors_clustered}
                    onChange={onChange}
                    disabled={existingProject && !canEditProjectDetails}
                  />
                  <label
                    htmlFor="sensors_clustered"
                    className="text-primary-off-black dark:text-primary-off-white transition duration-300"
                  >
                    {t("Sensors Clustered")}
                  </label>
                </div>
              </div>
              <div className={tailwindClassnames.formField}>
                <SelectInput
                  options={YesSomeNo.map((bait) => ({
                    value: bait.value,
                    label: t(bait.name),
                  }))}
                  className={"w-full"}
                  defaultValue={
                    formData.bait_used
                      ? {
                          value: formData.bait_used,
                          label: t(
                            YesSomeNo.find(
                              (bait) => bait.value === formData.bait_used,
                            )?.name || "",
                          ),
                        }
                      : null
                  }
                  isDisabled={!inputEditable}
                  errors={errors.bait_used}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectBaitUsed}
                  name={t("Bait Used") + "*"}
                  searchBar={false}
                />
              </div>
              {(formData.bait_used === "some" ||
                formData.bait_used === "yes") && (
                <div className={tailwindClassnames.formField}>
                  <SelectInput
                    options={BaitType.map((bait) => ({
                      value: bait.value,
                      label: t(bait.name),
                    }))}
                    className={"w-full"}
                    defaultValue={
                      formData.bait_type
                        ? {
                            value: formData.bait_type,
                            label: t(
                              BaitType.find(
                                (bait) => bait.value === formData.bait_type,
                              )?.name || "",
                            ),
                          }
                        : null
                    }
                    isDisabled={!inputEditable}
                    errors={errors.bait_type}
                    isLoading={false}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                    onChange={onSelectBaitType}
                    name={t("Bait Type") + "*"}
                    searchBar={false}
                  />
                </div>
              )}
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    id="project_stratified"
                    className="mt-0.5 accent-storm-blue dark:accent-sky-blue cursor-pointer transition duration-300"
                    type="checkbox"
                    name="project_stratified"
                    checked={formData.project_stratified}
                    onChange={onChange}
                    disabled={existingProject && !canEditProjectDetails}
                  />
                  <label
                    htmlFor="project_stratified"
                    className="text-primary-off-black dark:text-primary-off-white transition duration-300"
                  >
                    {t("Project Stratified") + "*"}
                  </label>
                </div>
              </div>
              {formData.project_stratified && (
                <div className={tailwindClassnames.formField}>
                  <TextInput
                    name="stratification_type"
                    label={t("Stratification Type")}
                    placeholder=""
                    value={formData.stratification_type || ""}
                    error={errors.stratification_type}
                    disabled={false}
                    className={inputClassName}
                    type="text"
                    initialFocus={false}
                    softLimit={FIELD_LIMITS.LONG_LINE}
                    onChange={onChange}
                  />
                </div>
              )}
            </div>
          </section>

          {/* SECTION: Data & Licensing */}
          <section className={tailwindClassnames.formSectionCard}>
            <div className={tailwindClassnames.formSectionHeader}>
              <div className={tailwindClassnames.formSectionTitle}>
                {t("Data & Licensing")}
              </div>
            </div>
            <div className={tailwindClassnames.formGrid}>
              <div className={tailwindClassnames.formField}>
                <SelectInput
                  options={YesSomeNo.map((option) => ({
                    value: option.value,
                    label: t(option.name),
                  }))}
                  className={"w-full"}
                  defaultValue={
                    formData.blank_images_removed
                      ? {
                          value: formData.blank_images_removed,
                          label: t(
                            YesSomeNo.find(
                              (option) =>
                                option.value === formData.blank_images_removed,
                            )?.name || "",
                          ),
                        }
                      : null
                  }
                  isDisabled={!inputEditable}
                  errors={errors.blank_images_removed}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectBlankImagesRemoved}
                  name={t("Blank Images Removed") + "*"}
                  searchBar={false}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <TextInput
                  name="coordinate_precision"
                  label={t("Coordinate Precision")}
                  labelSuffix={infoTooltip(
                    t(
                      "The least precise coordinate precision of the deployments' latitude and longitude (e.g. 0.01 for coordinates with a precision of 0.01 and 0.001 degree)",
                    ),
                  )}
                  placeholder=""
                  value={formData.coordinate_precision?.toString() || ""}
                  error={errors.coordinate_precision}
                  disabled={false}
                  className={inputClassName}
                  type="text"
                  initialFocus={false}
                  limit={255}
                  onChange={onChange}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <label
                  className={`${tailwindClassnames.textInputLabel} flex items-center`}
                >
                  {t("Photos License") + "*"}
                  {infoTooltip(licenseTooltipContent, true)}
                </label>
                <SelectInput
                  options={PhotosLicense.map((license) => ({
                    value: license.value,
                    label: license.name,
                  }))}
                  className={"w-full"}
                  defaultValue={
                    formData.photos_license
                      ? {
                          value: formData.photos_license,
                          label: PhotosLicense.find(
                            (license) =>
                              license.value === formData.photos_license,
                          )?.name,
                        }
                      : null
                  }
                  isDisabled={!inputEditable}
                  errors={errors.photos_license}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectPhotosLicense}
                  name=""
                  searchBar={false}
                />
              </div>
              <div className={tailwindClassnames.formField}>
                <label
                  className={`${tailwindClassnames.textInputLabel} flex items-center`}
                >
                  {t("Metadata License") + "*"}
                  {infoTooltip(licenseTooltipContent, true)}
                </label>
                <SelectInput
                  options={MetadataLicense.map((license) => ({
                    value: license.value,
                    label: license.name,
                  }))}
                  className={"w-full"}
                  defaultValue={
                    formData.metadata_license
                      ? {
                          value: formData.metadata_license,
                          label: MetadataLicense.find(
                            (license) =>
                              license.value === formData.metadata_license,
                          )?.name,
                        }
                      : null
                  }
                  isDisabled={!inputEditable}
                  errors={errors.metadata_license}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectMetadataLicense}
                  name=""
                  searchBar={false}
                />
              </div>
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <TextInput
                  name="citation"
                  label={t("Citation")}
                  placeholder={t("Bibliographic Citation")}
                  value={formData.citation || ""}
                  error={errors.citation}
                  disabled={false}
                  className={inputClassName}
                  type="text"
                  initialFocus={false}
                  softLimit={FIELD_LIMITS.NARRATIVE}
                  onChange={onChange}
                />
              </div>
              <div
                className={`${tailwindClassnames.formField} ${tailwindClassnames.formGridFull}`}
              >
                <TextInput
                  name="comments"
                  label={t("Comments")}
                  placeholder=""
                  value={formData.comments ? formData.comments : ""}
                  error={errors.comments}
                  disabled={!inputEditable}
                  className={inputClassName}
                  type="text"
                  initialFocus={false}
                  onChange={onChange}
                  softLimit={FIELD_LIMITS.NARRATIVE}
                  rows={4}
                  resizable={true}
                />
              </div>
            </div>
            {/* Hidden storage / SAS-token block - retained as-is (currently hidden) */}
            <div className={"ml-2 w-full flex flex-row mt-2 hidden"}>
              <FormControl>
                <FormLabel id="storage-option-radio-buttons-group"></FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="storage-option-radio-buttons-group"
                  name="storage-option-radio-buttons-group"
                  value="false"
                  onChange={onStorageOptionChange}
                >
                  <FormControlLabel
                    value="true"
                    control={
                      <Radio
                        sx={{
                          color: "grey",
                          "&.Mui-checked": {
                            color: isDarkMode
                              ? tailwindClassnames.skyBlue
                              : tailwindClassnames.stormBlue,
                          },
                        }}
                      />
                    }
                    className={
                      canSetSecret
                        ? tailwindClassnames.textInputLabel
                        : tailwindClassnames.textInputLabelDisabled
                    }
                    checked={formData.use_default_storage}
                    label="Default Storage"
                    disabled={!canSetSecret}
                  />
                  <FormControlLabel
                    value="false"
                    control={
                      <Radio
                        sx={{
                          color: "grey",
                          "&.Mui-checked": {
                            color: isDarkMode
                              ? tailwindClassnames.skyBlue
                              : tailwindClassnames.stormBlue,
                          },
                        }}
                      />
                    }
                    className={
                      canSetSecret
                        ? tailwindClassnames.textInputLabel
                        : tailwindClassnames.textInputLabelDisabled
                    }
                    checked={!formData.use_default_storage}
                    label="My Storage"
                    disabled={!canSetSecret}
                  />
                </RadioGroup>
              </FormControl>
            </div>
            <div className={"hidden w-full flex flex-row ml-2"}>
              {!formData.use_default_storage && (
                <div className="flex mt-1 w-full ">
                  <div className="w-full mr-6">
                    <TextInput
                      name="storage_url"
                      label={t("Storage URL")}
                      placeholder=""
                      value={formData.storage_url ? formData.storage_url : ""}
                      error={errors.storage_url}
                      disabled={!inputEditable}
                      className={inputClassName}
                      type="text"
                      initialFocus={false}
                      limit={FIELD_LIMITS.TEXT}
                      onChange={onChange}
                    />
                  </div>
                  <div className="w-full mr-6">
                    <TextInput
                      name="container_name"
                      label={t("Container Name")}
                      placeholder=""
                      value={
                        formData.container_name ? formData.container_name : ""
                      }
                      error={errors.container_name}
                      disabled={!inputEditable}
                      className={inputClassName}
                      type="text"
                      initialFocus={false}
                      limit={FIELD_LIMITS.TEXT}
                      onChange={onChange}
                    />
                  </div>
                  <div className="w-full flex flex-row items-start">
                    <div
                      className={
                        !existingProject || updatingSasToken ? "w-full" : ""
                      }
                    >
                      {!existingProject || updatingSasToken ? ( // only display password input if project is new or secret is being updated
                        <PasswordInput
                          name="sas_token"
                          label={t("SAS Token")}
                          placeholder=""
                          value={sasToken}
                          error={errors.sas_token}
                          disabled={false}
                          className={`${tailwindClassnames.detailsPageTextInput} w-full`}
                          initialFocus={false}
                          limit={FIELD_LIMITS.TEXT}
                          onChange={(e) => setSasToken(e.target.value)}
                        />
                      ) : (
                        // if the project exists, display a message that the secret is saved in key vault
                        <div className="mt-10">
                          {secretIsSet && (
                            <label
                              className={tailwindClassnames.textInputLabel}
                            >
                              {t("SAS Token saved in key vault.")}
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      {canSetSecret && existingProject && !updatingSasToken && (
                        <div className="mt-6">
                          <button
                            className={tailwindClassnames.btnSecondary}
                            onClick={() => {
                              setUpdatingSasToken(true);
                            }}
                          >
                            {t("New SAS Token")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* SECTION: Deployment Template */}
          <section className={tailwindClassnames.formSectionCard}>
            <div className={tailwindClassnames.formSectionHeader}>
              <div className={tailwindClassnames.formSectionTitle}>
                {t("Deployment Template")}
              </div>
            </div>
            <div className={tailwindClassnames.formField}>
              <label
                className={`${tailwindClassnames.textInputLabel} flex items-center`}
              >
                {t("Deployment Template")}
                {infoTooltip(
                  hasDeploymentsWithData
                    ? t(
                        "Schema template can't be changed because there are deployments with existing data",
                      )
                    : t(
                        "Select a template to define the data fields that will be available for your deployments.",
                      ),
                )}
              </label>
              <SelectInput
                options={
                  deploymentTemplates?.map((template) => ({
                    value: template.id,
                    label: template.name,
                  })) || []
                }
                name=""
                className="w-full"
                value={
                  formData.deployment_schema &&
                  Object.keys(formData.deployment_schema).length > 0
                    ? {
                        value: deploymentTemplates?.find(
                          (t) =>
                            JSON.stringify(t.schema_definition) ===
                            JSON.stringify(formData.deployment_schema),
                        )?.id,
                        label:
                          formData.deployment_schema?.title ||
                          deploymentTemplates?.find(
                            (t) =>
                              JSON.stringify(t.schema_definition) ===
                              JSON.stringify(formData.deployment_schema),
                          )?.name ||
                          "Custom Schema",
                      }
                    : undefined
                }
                isDisabled={
                  (existingProject && !canEditProjectDetails) ||
                  !deploymentTemplates?.length ||
                  hasDeploymentsWithData
                }
                isLoading={!deploymentTemplates}
                errors={errors.schema_type}
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                onChange={onSelectSchemaTemplate}
                searchBar={false}
              />
            </div>
            {/* comment below in for custom fields */}
            {schemaLoading && (
              <div className="w-full flex justify-center mt-8">
                <BeatLoader
                  color={"#272837"}
                  loading={true}
                  size={15}
                  margin={5}
                />
              </div>
            )}
          </section>
        </div>

        <div className={tailwindClassnames.formActionBar}>
          <button
            className={tailwindClassnames.btnSecondary}
            type="button"
            onClick={() => onCancel()}
          >
            {t("Cancel")}
          </button>

          {(!getIsReadOnlyUser() || profile.is_admin) && (
            <button
              className={`relative ${tailwindClassnames.btnPrimary}`}
              type="submit"
              onClick={onSave}
              disabled={saveDisabled}
            >
              {/* Label stays in layout while saving so the button keeps its
                  width; currentColor spinner reads on the fill in both
                  themes. */}
              <span className={isSaving ? "invisible" : ""}>{t("Save")}</span>
              {isSaving && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <BeatLoader
                    color="currentColor"
                    loading={true}
                    size={10}
                    margin={5}
                  />
                </span>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProjectDetailsOverview;
